import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Grid,
    CircularProgress,
    MenuItem,
    Chip,
    Card,
    CardContent,
    Typography,
    Box,
    TableSortLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { honorariosResumenJWT, honorariosSolicitudesJWT } from "../../api/endpoints";
import moment from "moment";

const sxStatsCard = {
    cursor: "pointer",
    transition: "all 0.3s ease",
    borderRadius: "4px",
    "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    },
};

const SolicitudesTable = forwardRef((props, ref) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState({
        fecha_desde: moment().subtract(1, "year").format("YYYY-MM-DD"),
        fecha_hasta: moment().format("YYYY-MM-DD"),
        estado: "100", // All
    });
    const [stats, setStats] = useState({
        cobrado: { total: 0, certificados: 0 },
        en_proceso: { total: 0, solicitudes: 0 },
        pendiente: { total: 0, certificados: 0 },
    });
    const [activeStatCard, setActiveStatCard] = useState(null);
    const [orderBy, setOrderBy] = useState('Id');
    const [order, setOrder] = useState('desc');

    const handleSort = (column) => {
        const isAsc = orderBy === column && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(column);
    };

    const sortedData = [...data].sort((a, b) => {
        let aVal = a[orderBy];
        let bVal = b[orderBy];
        // Parse dd/MM/yyyy dates for proper sorting
        if (orderBy === 'Fecha' || orderBy === 'Fecha_Pago') {
            const parseDate = (d) => {
                if (!d || d === '-') return 0;
                const parts = d.split('/');
                return parts.length === 3 ? new Date(parts[2], parts[1] - 1, parts[0]).getTime() : 0;
            };
            aVal = parseDate(aVal);
            bVal = parseDate(bVal);
        }
        if (typeof aVal === 'string') aVal = aVal?.toLowerCase() || '';
        if (typeof bVal === 'string') bVal = bVal?.toLowerCase() || '';
        if (aVal < bVal) return order === 'asc' ? -1 : 1;
        if (aVal > bVal) return order === 'asc' ? 1 : -1;
        return 0;
    });

    const fetchData = async (currentFilters = filters) => {
        setLoading(true);
        try {
            // La API solo filtra 1 (pagadas) y 0 (pend/pagadas); para anuladas enviamos vacio y filtramos client-side
            const estadoParam = currentFilters.estado === "-1" ? "" : currentFilters.estado;
            const response = await honorariosSolicitudesJWT(currentFilters.fecha_desde, currentFilters.fecha_hasta, estadoParam);
            if (response.ok) {
                const lista = response.data || [];
                const filtered = currentFilters.estado === "-1"
                    ? lista.filter(item => Number(item.Id_Estado) === -1)
                    : lista;
                setData(filtered);
            }
        } catch (error) {
            if (error?.code === "SESSION_EXPIRED") {
                throw error;
            }
            console.error("Error fetching solicitudes", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async (currentFilters = filters) => {
        try {
            const response = await honorariosResumenJWT(currentFilters.fecha_desde, currentFilters.fecha_hasta);
            if (response.ok) {
                setStats({
                    cobrado: response.cobrado || { total: 0, certificados: 0 },
                    en_proceso: response.en_proceso || { total: 0, solicitudes: 0 },
                    pendiente: response.pendiente || { total: 0, certificados: 0 },
                });
            }
        } catch (error) {
            if (error?.code === "SESSION_EXPIRED") {
                throw error;
            }
            console.error("Error fetching stats", error);
        }
    };

    useEffect(() => {
        fetchData(filters);
        fetchStats(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Expose refresh method to parent
    useImperativeHandle(ref, () => ({
        refresh: () => {
            fetchData(filters);
            fetchStats(filters);
        }
    }));

    const handleFilterChange = (prop) => (event) => {
        setFilters({ ...filters, [prop]: event.target.value });
    };

    const handleSearch = () => {
        fetchData(filters);
        fetchStats(filters);
    };

    const handleStatCardClick = (cardType) => {
        // Toggle selection if clicking the same card
        if (activeStatCard === cardType) {
            setActiveStatCard(null);
            setFilters({ ...filters, estado: "100" }); // Reset to "Todos"
            fetchData({ ...filters, estado: "100" });
        } else {
            setActiveStatCard(cardType);
            let nuevoEstado = "100";

            switch (cardType) {
                case "cobrado":
                    nuevoEstado = "1"; // Pagada
                    break;
                case "en_proceso":
                    nuevoEstado = "2"; // En Curso (puede filtrar 2 y 4)
                    break;
                case "pendiente":
                    nuevoEstado = "0"; // Pendiente
                    break;
                default:
                    nuevoEstado = "100";
            }

            setFilters({ ...filters, estado: nuevoEstado });
            fetchData({ ...filters, estado: nuevoEstado });
        }
    };

    const getStatusColor = (statusId) => {
        switch (statusId) {
            case 1: return "primary"; // Pagada
            case 0: return "default"; // Pendiente
            case 2: return "secondary"; // En curso
            case 3: return "primary"; // Borrador
            case 4: return "default"; // Cerrada
            case -1: return "secondary"; // Anulada
            default: return "default";
        }
    };

    const getStatusLabel = (statusId) => {
        switch (statusId) {
            case 1: return "Pagada";
            case 0: return "Pendiente";
            case 2: return "En Curso";
            case 3: return "Borrador";
            case 4: return "Enviada";
            case -1: return "Anulada";
            default: return "Desconocido";
        }
    };

    const handleRowClick = (solicitudId) => {
        navigate(`/honorarios/solicitud/${solicitudId}`);
    };

    return (
        <div>
            {/* Stats Cards */}
            <Box mb={2}>
                <Grid container spacing={2}>
                    {/* Cobrado */}
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Card
                            sx={{
                                ...sxStatsCard,
                                ...(activeStatCard === "cobrado" ? { borderWidth: 2, borderStyle: "solid" } : {}),
                            }}
                            onClick={() => handleStatCardClick("cobrado")}
                            style={{
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "white",
                                borderColor: activeStatCard === "cobrado" ? "#4a5568" : "transparent",
                            }}
                        >
                            <CardContent style={{ padding: 16 }}>
                                <Box display="flex" flexDirection="column" alignItems="center">
                                    <CheckCircleIcon sx={{ fontSize: 36, mb: 0.5 }} />
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(255,255,255,0.9)" }}>
                                        Honorarios Cobrados
                                    </Typography>
                                    <Typography sx={{ fontSize: 24, fontWeight: 700, mt: 0.25, mb: 0.25 }}>
                                        $ {stats.cobrado.total.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>
                                        {stats.cobrado.certificados} {stats.cobrado.certificados === 1 ? "solicitud" : "solicitudes"}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* En Proceso */}
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Card
                            sx={{
                                ...sxStatsCard,
                                ...(activeStatCard === "en_proceso" ? { borderWidth: 2, borderStyle: "solid" } : {}),
                            }}
                            onClick={() => handleStatCardClick("en_proceso")}
                            style={{
                                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                color: "white",
                                borderColor: activeStatCard === "en_proceso" ? "#4a5568" : "transparent",
                            }}
                        >
                            <CardContent style={{ padding: 16 }}>
                                <Box display="flex" flexDirection="column" alignItems="center">
                                    <HourglassEmptyIcon sx={{ fontSize: 36, mb: 0.5 }} />
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(255,255,255,0.9)" }}>
                                        En Proceso
                                    </Typography>
                                    <Typography sx={{ fontSize: 24, fontWeight: 700, mt: 0.25, mb: 0.25 }}>
                                        $ {stats.en_proceso.total.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>
                                        {stats.en_proceso.solicitudes} {stats.en_proceso.solicitudes === 1 ? "solicitud" : "solicitudes"}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Pendiente */}
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Card
                            sx={{
                                ...sxStatsCard,
                                ...(activeStatCard === "pendiente" ? { borderWidth: 2, borderStyle: "solid" } : {}),
                            }}
                            onClick={() => handleStatCardClick("pendiente")}
                            style={{
                                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                                color: "white",
                                borderColor: activeStatCard === "pendiente" ? "#4a5568" : "transparent",
                            }}
                        >
                            <CardContent style={{ padding: 16 }}>
                                <Box display="flex" flexDirection="column" alignItems="center">
                                    <AttachMoneyIcon sx={{ fontSize: 36, mb: 0.5 }} />
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(255,255,255,0.9)" }}>
                                        Honorarios Pendientes
                                    </Typography>
                                    <Typography sx={{ fontSize: 24, fontWeight: 700, mt: 0.25, mb: 0.25 }}>
                                        $ {stats.pendiente.total.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>
                                        {stats.pendiente.certificados} {stats.pendiente.certificados === 1 ? "certificado" : "certificados"}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                        label="Desde"
                        type="date"
                        value={filters.fecha_desde}
                        onChange={handleFilterChange("fecha_desde")}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                        label="Hasta"
                        type="date"
                        value={filters.fecha_hasta}
                        onChange={handleFilterChange("fecha_hasta")}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                        select
                        label="Estado"
                        value={filters.estado}
                        onChange={handleFilterChange("estado")}
                        fullWidth
                    >
                        <MenuItem value="100">Todos</MenuItem>
                        <MenuItem value="3">Borrador</MenuItem>
                        <MenuItem value="4">Enviada</MenuItem>
                        <MenuItem value="2">En Curso</MenuItem>
                        <MenuItem value="1">Pagada</MenuItem>
                        <MenuItem value="-1">Anulada</MenuItem>
                    </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        fullWidth
                    >
                        Buscar
                    </Button>
                </Grid>
            </Grid>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ overflowX: "auto", maxWidth: "100%" }}>
                    <Table sx={{ minWidth: 650 }} size="small" aria-label="solicitudes table">
                        <TableHead>
                            <TableRow>
                                {[
                                    { id: 'Id', label: 'Nº Solicitud' },
                                    { id: 'Fecha', label: 'Fecha' },
                                    { id: 'Certificados', label: 'Certificados' },
                                    { id: 'Honorarios', label: 'Honorarios', align: 'right' },
                                    { id: 'Id_Estado', label: 'Estado', align: 'center' },
                                    { id: 'Fecha_Pago', label: 'Fecha Pago', align: 'center' },
                                ].map(col => (
                                    <TableCell key={col.id} align={col.align} sortDirection={orderBy === col.id ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === col.id}
                                            direction={orderBy === col.id ? order : 'asc'}
                                            onClick={() => handleSort(col.id)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">No se encontraron solicitudes.</TableCell>
                                </TableRow>
                            ) : (
                                sortedData.map((row, idx) => (
                                    <TableRow
                                        key={row.Id}
                                        hover
                                        onClick={() => handleRowClick(row.Id)}
                                        sx={{
                                            cursor: "pointer",
                                            "&:hover": { bgcolor: "action.hover" },
                                            backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent",
                                        }}
                                    >
                                        <TableCell>{row.Id}</TableCell>
                                        <TableCell>{row.Fecha}</TableCell>
                                        <TableCell>{row.Certificados}</TableCell>
                                        <TableCell align="right">{row.Honorarios?.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={getStatusLabel(row.Id_Estado)}
                                                color={getStatusColor(row.Id_Estado)}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="center">{row.Fecha_Pago || "-"}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </div>
    );
});

export default SolicitudesTable;
